import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, tap } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { LocalDate } from '../../../core/model/local-date';
import { DataService } from '../../../core/service/base/data.service';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import {
  CompanyBankAccount,
  CompanyBankAccountCreate,
  CompanyBankAccountUpdate,
  toCompanyBankAccountUpdate,
} from '../../../rent/model/company-bank-account';

@Injectable()
export class CompanyBankAccountService extends DataService<
  CompanyBankAccount,
  CompanyBankAccountCreate,
  CompanyBankAccountUpdate,
  CompanyBankAccount
> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, CompanyBankAccount, progressBar, 'company-bank-account');
  }

  createNew(model: CompanyBankAccountCreate): Observable<CompanyBankAccount> {
    this.progressBar.start();
    return this.client.post<CompanyBankAccount>(this.endpoint, model).pipe(
      map(account => this.toBankAccount(account)),
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  updateExisting(model: CompanyBankAccountUpdate): Observable<CompanyBankAccount> {
    this.progressBar.start();
    return this.client.put<CompanyBankAccount>(this.endpoint, model).pipe(
      map(account => this.toBankAccount(account)),
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  getByCompany(companyId: string): Observable<CompanyBankAccount[]> {
    this.progressBar.start();
    return this.client.get<CompanyBankAccount[]>(`${this.endpoint}/get-by-company/${companyId}`).pipe(
      map(accounts => accounts.map(account => this.toBankAccount(account))),
      finalize(() => this.progressBar.stop())
    );
  }

  updateBankTransactionImportStartDate(companyBankAccount: CompanyBankAccount): Observable<CompanyBankAccount> {
    this.progressBar.start();
    return this.client.put<CompanyBankAccount>(this.endpoint, toCompanyBankAccountUpdate(companyBankAccount)).pipe(
      map(account => this.toBankAccount(account)),
      tap(() => this.notification.showSuccess('SUCCESS.BANK_IMPORT_TRANSACTIONS_START_DATE_UPDATED')),
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  private toBankAccount(account: CompanyBankAccount): CompanyBankAccount {
    return Object.assign(new CompanyBankAccount(), account, {
      transactionImportedAt: LocalDate.fromDate(account.transactionImportedAt),
      bankTransactionsImportStartDate: LocalDate.fromDate(account.bankTransactionsImportStartDate),
    });
  }
}
